import algoliasearch from 'algoliasearch'
import { MultipleQueriesQuery, SearchResponse } from '@algolia/client-search'
import { type CollectionProductsPage } from './Products/types'
import { type QueryFunctionContext } from '@tanstack/react-query'
import type { ProductHit } from '@/features/product/types'
import { clamp } from '@lounge-fe/ui-kit'
import {
  filtersToFacets,
  createAvailableFilters,
  getAlgoliaStockQuery,
  isFilteringByOptions,
} from '@/services/algolia/utils'
import { requestOptions } from '@/services/algolia'

interface QueryOptions extends QueryFunctionContext {
  index: string
  query?: string
  filters: { [key: string]: string }
  tags: string[]
  offset?: number
  length?: number
  sort?: string
  algolia: {
    indexPrefix: string
    appId: string
    apiKey: string
  }
  useSwatches: boolean
}

/**
 * This function performs a search query to Algolia, including a query for available facet filters.
 * It then returns a page of product results, along with available filters and the total count of results.
 *
 * TODO: Add tests for this function
 *
 * @param {Object} options - An object containing options for the search query.
 * @returns {Promise<CollectionProductsPage>} - A promise that resolves to a page of search results, including available filters and the total count of results.
 */
export const query = async ({
  query = '',
  filters,
  offset = 0,
  length = 24,
  sort,
  useSwatches = false,
}: QueryOptions): Promise<CollectionProductsPage> => {
  const { algolia } = window.Lounge.integrations
  const { stop_selling_quantity = 0 } = window.Lounge.settings

  const { indexPrefix, apiKey, appId } = algolia

  const algoliaClient = algoliasearch(appId, apiKey)

  const facetFilters = filtersToFacets(filters)

  const filterStockQuery = getAlgoliaStockQuery(
    indexPrefix,
    stop_selling_quantity,
  )

  const index = useSwatches
    ? `${indexPrefix}_products_swatches`
    : `${indexPrefix}_products`

  const sortIndex = useSwatches
    ? `${indexPrefix}_products_swatches_sort_${sort}`
    : `${indexPrefix}_products_sort_${sort}`

  const indexName = sort ? sortIndex : index

  const queries: MultipleQueriesQuery[] = [
    {
      indexName,
      query,
      params: {
        ...requestOptions,
        offset,
        length,
        facetFilters: filters?.collections
          ? [`collections:${filters?.collections}`]
          : undefined,
        clickAnalytics: true,
      },
    },
    {
      indexName,
      query,
      params: {
        ...requestOptions,
        facetFilters,
        offset,
        length,
        filters: isFilteringByOptions(facetFilters) ? filterStockQuery : '',
        clickAnalytics: true,
      },
    },
  ]

  const {
    results: [facets, products],
  } = await algoliaClient.multipleQueries<ProductHit>(queries)

  const endOffset = Math.min(
    products.nbHits,
    offset + (products.length ?? length),
  )

  return {
    products: products.hits,
    queryID: (products as SearchResponse & { queryID: string }).queryID,
    availableFilters: createAvailableFilters(
      facets.facets,
      facets.renderingContent?.facetOrdering,
    ),
    totalCount: products.nbHits,
    startOffset: offset,
    endOffset: clamp(endOffset, 0, products.nbHits),
  }
}
